import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Megaphone } from 'lucide-react';
import { announcementAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';

const AnnouncementModal = () => {
  const { user } = useContext(AuthContext);
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (user) {
      loadAnnouncements();
    }
  }, [user]);

  const loadAnnouncements = async () => {
    try {
      const response = await announcementAPI.getUnread();
      // Vérifier que c'est bien un tableau avec des annonces valides
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Filtrer les annonces valides (avec titre et message)
        const validAnnouncements = response.data.filter(a => a && a.id && a.titre && a.message);
        if (validAnnouncements.length > 0) {
          setAnnouncements(validAnnouncements);
          setCurrentIndex(0);
          setIsVisible(true);
        }
      }
    } catch (error) {
      // Silencieux - pas d'annonces disponibles
    }
  };

  // Formater la date de manière sécurisée
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const handleClose = async () => {
    const currentAnnouncement = announcements[currentIndex];
    
    // Marquer comme lu
    try {
      await announcementAPI.markAsRead(currentAnnouncement.id);
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }

    // Animation de fermeture
    setIsClosing(true);
    
    setTimeout(() => {
      setIsClosing(false);
      
      // Passer à l'annonce suivante ou fermer
      if (currentIndex < announcements.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setIsVisible(false);
        setAnnouncements([]);
      }
    }, 300);
  };

  if (!isVisible || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  const hasMultiple = announcements.length > 1;

  // Utiliser un portal pour rendre le modal directement dans le body
  return ReactDOM.createPortal(
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-lg transform transition-all duration-300 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header avec icône */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Megaphone className="w-5 h-5" /> Annonce DJAMANN
                </h3>
                {currentAnnouncement.author_name && (
                  <p className="text-white/70 text-sm">
                    De: {currentAnnouncement.author_name}
                  </p>
                )}
              </div>
              {hasMultiple && (
                <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm">
                  {currentIndex + 1}/{announcements.length}
                </span>
              )}
            </div>
          </div>
          
          {/* Contenu */}
          <div className="p-6">
            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              {currentAnnouncement.titre}
            </h4>
            <div className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
              {currentAnnouncement.message}
            </div>
            
            {/* Date */}
            {formatDate(currentAnnouncement.created_at) && (
              <p className="mt-4 text-sm text-gray-400">
                {formatDate(currentAnnouncement.created_at)}
              </p>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex justify-end">
            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {hasMultiple && currentIndex < announcements.length - 1 
                ? 'Suivant →' 
                : 'Compris ✓'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnouncementModal;
